// MBR homepage interactions: reveal effects, reviews, product cards, and catalog pagination.

(() => {
  const root = document.querySelector('[data-mbr-homepage]');

  if (!root) return;

  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-visible');
              revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -12% 0px', threshold: 0.18 }
        )
      : null;

  const observeRevealElements = (scope) => {
    scope.querySelectorAll('.mbr-fade-up:not(.is-visible)').forEach((item) => {
      if (revealObserver) revealObserver.observe(item);
      else item.classList.add('is-visible');
    });
  };

  observeRevealElements(root);

  const initializedCards = new WeakSet();

  const initializeProductCard = (card) => {
    if (initializedCards.has(card)) return;

    const variantsElement = card.querySelector('[data-mbr-variants]');
    if (!variantsElement) return;

    let variants;

    try {
      variants = JSON.parse(variantsElement.textContent);
    } catch (error) {
      console.error('Unable to load MBR product variants.', error);
      return;
    }

    const optionGroups = Array.from(card.querySelectorAll('[data-mbr-option]'));
    const variantInput = card.querySelector('input[name="id"]');
    const quantityInput = card.querySelector('input[name="quantity"]');
    const productImage = card.querySelector('.mbr-card__image');
    const priceElement = card.querySelector('[data-mbr-price]');
    const compareElement = card.querySelector('[data-mbr-compare]');
    const stockElement = card.querySelector('[data-mbr-stock]');
    const statusElement = card.querySelector('[data-mbr-status]');
    const addButton = card.querySelector('.mbr-add-to-cart');
    const addLabel = card.querySelector('[data-mbr-add-label]');
    const buyButton = card.querySelector('[data-mbr-buy-now]');
    const buyLabel = card.querySelector('[data-mbr-buy-label]');
    const productForm = card.querySelector('.mbr-card__form-inner');
    const currency = card.dataset.currency || 'USD';
    const locale = card.dataset.locale || document.documentElement.lang || 'en';

    if (
      !variantInput ||
      !quantityInput ||
      !priceElement ||
      !compareElement ||
      !stockElement ||
      !statusElement ||
      !addButton ||
      !addLabel ||
      !buyButton ||
      !buyLabel ||
      !productForm
    ) {
      return;
    }

    const formatMoney = (cents) => {
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
      } catch (_error) {
        return `${(cents / 100).toFixed(2)} ${currency}`;
      }
    };

    const selectedOptions = () =>
      optionGroups.map((group) => group.querySelector('input:checked')?.value).filter((value) => value !== undefined);

    const findSelectedVariant = () => {
      if (!optionGroups.length) {
        return variants.find((variant) => String(variant.id) === String(variantInput.value)) || variants[0];
      }

      const selections = selectedOptions();
      return variants.find(
        (variant) =>
          variant.options.length === selections.length &&
          variant.options.every((option, index) => option === selections[index])
      );
    };

    const setStatus = (message = '', type = '') => {
      statusElement.textContent = message;
      statusElement.classList.toggle('is-success', type === 'success');
      statusElement.classList.toggle('is-error', type === 'error');
    };

    const updateOptionAvailability = () => {
      optionGroups.forEach((group, optionIndex) => {
        const inputs = Array.from(group.querySelectorAll('input[type="radio"]'));
        const priorSelections = selectedOptions().slice(0, optionIndex);

        inputs.forEach((input) => {
          input.disabled = !variants.some(
            (variant) =>
              variant.available &&
              variant.options[optionIndex] === input.value &&
              priorSelections.every((selection, index) => variant.options[index] === selection)
          );
        });

        const checkedInput = group.querySelector('input:checked');
        if (checkedInput?.disabled) {
          const firstAvailableInput = inputs.find((input) => !input.disabled);
          if (firstAvailableInput) firstAvailableInput.checked = true;
        }
      });
    };

    const updateProduct = () => {
      updateOptionAvailability();
      const variant = findSelectedVariant();
      const available = Boolean(variant?.available);

      variantInput.value = variant?.id || '';
      variantInput.disabled = !available;
      addButton.disabled = !available;
      buyButton.disabled = !available;
      addLabel.textContent = available ? 'Add to Cart' : 'Sold Out';
      stockElement.textContent = available ? 'In stock' : 'Sold out';
      stockElement.classList.toggle('is-sold-out', !available);

      if (!variant) {
        priceElement.textContent = '';
        compareElement.textContent = '';
        compareElement.classList.add('is-hidden');
        setStatus('This option combination is unavailable.', 'error');
        return;
      }

      priceElement.textContent = formatMoney(variant.price);
      const onSale = variant.compareAtPrice && variant.compareAtPrice > variant.price;
      compareElement.textContent = onSale ? formatMoney(variant.compareAtPrice) : '';
      compareElement.classList.toggle('is-hidden', !onSale);

      if (variant.image && productImage) {
        productImage.src = variant.image.src;
        productImage.alt = variant.image.alt || '';
        productImage.width = variant.image.width;
        productImage.height = variant.image.height;
        productImage.removeAttribute('srcset');
      }

      setStatus();
    };

    optionGroups.forEach((group) => group.addEventListener('change', updateProduct));

    card.querySelector('[data-mbr-quantity-minus]')?.addEventListener('click', () => {
      quantityInput.value = Math.max(1, Number.parseInt(quantityInput.value, 10) - 1 || 1);
    });

    card.querySelector('[data-mbr-quantity-plus]')?.addEventListener('click', () => {
      quantityInput.value = Math.max(1, Number.parseInt(quantityInput.value, 10) + 1 || 1);
    });

    quantityInput.addEventListener('change', () => {
      quantityInput.value = Math.max(1, Number.parseInt(quantityInput.value, 10) || 1);
    });

    productForm.addEventListener('submit', () => {
      setStatus('Adding to cart…');
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source !== 'product-form' || String(event.productVariantId) !== String(variantInput.value)) return;
        setStatus('Added to cart.', 'success');
      });

      subscribe(PUB_SUB_EVENTS.cartError, (event) => {
        if (event.source !== 'product-form' || String(event.productVariantId) !== String(variantInput.value)) return;
        setStatus(event.errors || event.message || 'Unable to add this item.', 'error');
      });
    }

    buyButton.addEventListener('click', async () => {
      const variant = findSelectedVariant();
      if (!variant?.available || buyButton.classList.contains('loading')) return;

      buyButton.disabled = true;
      buyButton.classList.add('loading');
      buyButton.querySelector('.loading__spinner')?.classList.remove('hidden');
      buyLabel.textContent = 'Redirecting…';
      setStatus('Preparing secure checkout…');

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [{ id: variant.id, quantity: Number.parseInt(quantityInput.value, 10) || 1 }],
          }),
        });
        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Unable to start checkout.');
        }

        setStatus('Item added. Redirecting to checkout…', 'success');
        const checkoutUrl = window.routes.cart_url.replace(/\/cart\/?$/, '/checkout');
        window.location.assign(checkoutUrl);
      } catch (error) {
        setStatus(error.message || 'Unable to start checkout.', 'error');
        buyButton.disabled = false;
        buyButton.classList.remove('loading');
        buyButton.querySelector('.loading__spinner')?.classList.add('hidden');
        buyLabel.textContent = 'Buy Now';
      }
    });

    updateProduct();
    initializedCards.add(card);
  };

  const initializeCards = (scope) => {
    scope.querySelectorAll('[data-mbr-product-card]').forEach(initializeProductCard);
    observeRevealElements(scope);
  };

  initializeCards(root);

  const catalog = root.querySelector('[data-mbr-catalog]');
  const productGrid = catalog?.querySelector('[data-mbr-product-grid]');
  const catalogStatus = catalog?.querySelector('[data-mbr-catalog-status]');

  const createSkeletons = () => {
    if (!productGrid) return [];

    return Array.from({ length: 4 }, () => {
      const skeleton = document.createElement('div');
      skeleton.className = 'mbr-skeleton-card';
      skeleton.setAttribute('aria-hidden', 'true');
      productGrid.appendChild(skeleton);
      return skeleton;
    });
  };

  const initializeLoadMore = (button) => {
    if (!button || button.dataset.mbrInitialized === 'true') return;
    button.dataset.mbrInitialized = 'true';

    button.addEventListener('click', async (event) => {
      event.preventDefault();
      if (button.classList.contains('loading') || !productGrid) return;

      button.classList.add('loading');
      button.setAttribute('aria-disabled', 'true');
      button.querySelector('.loading__spinner')?.classList.remove('hidden');
      catalogStatus.textContent = 'Loading more products…';
      catalogStatus.classList.remove('is-error');
      const skeletons = createSkeletons();

      try {
        const response = await fetch(button.href, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error('Unable to load more products.');

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const incomingGrid = documentFragment.querySelector('[data-mbr-product-grid]');
        const incomingCards = incomingGrid ? Array.from(incomingGrid.children) : [];

        if (!incomingCards.length) throw new Error('No additional products were returned.');

        incomingCards.forEach((card) => productGrid.appendChild(card));
        initializeCards(productGrid);

        const nextButton = documentFragment.querySelector('[data-mbr-load-more]');
        if (nextButton) {
          button.href = nextButton.getAttribute('href');
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
          button.querySelector('.loading__spinner')?.classList.add('hidden');
        } else {
          button.remove();
        }

        catalogStatus.textContent = `${incomingCards.length} more products loaded.`;
      } catch (error) {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
        button.querySelector('.loading__spinner')?.classList.add('hidden');
        catalogStatus.textContent = error.message || 'Unable to load more products. Please try again.';
        catalogStatus.classList.add('is-error');
      } finally {
        skeletons.forEach((skeleton) => skeleton.remove());
      }
    });
  };

  initializeLoadMore(catalog?.querySelector('[data-mbr-load-more]'));
})();
